import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { hybridAuthStorage } from '@/integrations/supabase/authStorage';
import type { SessionInvalidReason } from '@/hooks/useSessionValidation';

/**
 * Server-side session revocation for a logout — multi-device edition.
 *
 * One account may stay logged in on several devices at once. A logout (whether
 * user-initiated, forced, or a cross-tab echo) must end ONLY the current
 * device's session, so it ALWAYS revokes with `scope: 'local'`. A
 * `scope: 'global'` revoke would invalidate the GoTrue refresh tokens of every
 * other device and log the user out everywhere, which is exactly what we must
 * not do here.
 *
 * The classic "log out, then auto-logged in" bug (a residual refresh token —
 * an in-flight auto-refresh or a second tab — silently re-establishing the
 * session) is no longer countered by a global revoke. It is handled by the
 * caller's bulletproof local teardown (localStorage purge + BroadcastChannel in
 * AuthContext), which runs regardless of what this function does.
 *
 * The `reason` and `skipBroadcast` parameters are kept for call-site
 * compatibility (the AuthContext caller passes both positionally) but no longer
 * change behaviour: every path is local scope. The old single-session
 * `clear_active_session` RPC is gone with multi-device.
 *
 * Never throws — logout must always proceed to local state teardown.
 */
export async function revokeSessionForLogout(
  _reason?: SessionInvalidReason,
  _skipBroadcast = false,
): Promise<void> {
  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (e) {
    // Local teardown (storage purge, BroadcastChannel) still happens in the
    // caller, so a failed signOut must never block logout.
    console.error('local signOut failed during logout', e);
  }
}

/**
 * Leave the authenticated app after a logout, landing on the public marketing page.
 *
 * On WEB we hard-redirect to the marketing landing (`/fr`) with a full page reload.
 * This is deliberate: an in-app `signOut` sets `initialAuthDone` to false and nothing
 * resets it without a reload, so SPA-navigating to `/` would mount `RedirectToMarketing`
 * which can deadlock on its loading spinner (it waits forever on `!initialAuthDone`).
 * A full reload re-inits AuthProvider cleanly and also bulletproofs against any
 * residual-session auto-resume. On NATIVE (Capacitor), `/` renders `NativeAppHome`
 * (not `RedirectToMarketing`), so ordinary SPA navigation is correct there.
 *
 * @param navigate - react-router navigate function, used on native only.
 */
export function leaveToPublicRoute(navigate: (path: string) => void): void {
  if (Capacitor.isNativePlatform()) {
    navigate('/');
  } else {
    window.location.replace('/fr');
  }
}

/**
 * Jette la session de CET appareil, même quand le serveur ne répond pas.
 *
 * Révoque en portée locale (jamais globale, voir plus haut) ; si la révocation
 * échoue (erreur rendue OU levée) et qu'une session reste dans le navigateur,
 * la purge quand même. Ne lève jamais.
 *
 * POURQUOI la purge forcée : sur une coupure réseau, `signOut` (auth-js 2.89,
 * `GoTrueClient._signOut`) rend `{ error }` (statut 0) SANS appeler
 * `_removeSession()`, quel que soit le `scope` : seuls les 401, 403 et 404 du
 * serveur sont ignorés. La session restait alors vivante dans le navigateur
 * pendant que l'écran revenait au courriel. Constat C1 du lot 2 du tunnel /book.
 *
 * `_removeSession()` (GoTrueClient.js:2063) retire la session du stockage ET
 * émet `SIGNED_OUT`, ce qui remet `user` à null dans AuthContext. Méthode
 * interne, donc atteinte avec prudence : si elle manque ou lève (auth-js mis à
 * jour), repli sur le retrait direct des clés par l'adaptateur de stockage du
 * client (`hybridAuthStorage` : sessionStorage, localStorage et coffre natif),
 * sans l'événement.
 * Le jeton reste valide côté serveur jusqu'à son expiration, mais plus aucun
 * code de ce navigateur ne le connaît.
 */
export async function discardLocalSession(): Promise<void> {
  let revokeFailed = false;
  try {
    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) revokeFailed = true;
  } catch (e) {
    revokeFailed = true;
    console.error('local signOut failed, forcing local session purge', e);
  }
  if (!revokeFailed) return;

  try {
    const { data } = await supabase.auth.getSession();
    if (!data.session) return;
  } catch {
    // Lecture impossible : on purge par prudence.
  }

  const auth = supabase.auth as unknown as {
    _removeSession?: () => Promise<void>;
    storageKey?: string;
  };
  try {
    if (typeof auth._removeSession === 'function') {
      await auth._removeSession();
      return;
    }
  } catch (e) {
    console.error('forced local session purge failed, clearing storage', e);
  }

  try {
    const key = auth.storageKey;
    if (key) {
      // Mêmes clés que `_removeSession`.
      await hybridAuthStorage.removeItem(key);
      await hybridAuthStorage.removeItem(`${key}-code-verifier`);
      await hybridAuthStorage.removeItem(`${key}-user`);
    }
  } catch {
    // Stockage inaccessible : plus rien à tenter.
  }
}
